/* Choosing a variant on the page, with the options in sight.
 *
 * The picker is rendered by Liquid and works on its own: every option value
 * is a link to ?variant=id, so with no JavaScript a shopper picks, the page
 * reloads on that variant, and the buy bar adds it. That is the floor and it
 * is untouched. This upgrades the links to buttons so a choice costs a
 * repaint instead of a page load.
 *
 * It replaces a bottom sheet that opened when add was pressed. The sheet
 * existed for a real reason - the buy bar is pinned to the bottom of a phone,
 * so add was reachable from anywhere while the picker was only near the top,
 * and someone could add the first scent without ever seeing there were five.
 * But it solved that by HIDING the picker and putting a one-line summary in
 * its place, so the range was invisible until you pressed a button that did
 * not say it would show you anything.
 *
 * Showing the options instead solves the same problem earlier and without a
 * second surface: they are passed on the way down the page, and there is one
 * control rather than two views of one. Which also settles the objection the
 * sheet raised on its own - that add-then-choose and choose-then-add were
 * two flows for one decision.
 *
 * Products whose only option has a single value are left alone. 111 in this
 * catalogue are like that, 56 of them Shopify's "Title / Default Title", and
 * a picker with one button is not a choice.
 */

const form = document.getElementById('ksg-buy-form');
const pickers = [...document.querySelectorAll('.ksg-variants')];

/* A value worth choosing between. One option value is not a choice. */
const hasChoice = pickers.some((p) => p.querySelectorAll('.ksg-variant').length > 1);

if (form && pickers.length && hasChoice) {
  const idField = form.querySelector('input[name="id"]');

  /* The options-to-variant table, so a choice resolves against every option
     at once instead of one at a time. Eleven scents by three sizes is a small
     table and it is the same one the server has; shipping it is cheaper than
     being wrong. Without it, picking a scent and then a size left the form on
     the first variant carrying that size rather than on that size OF that
     scent. */
  let variants = [];
  try {
    variants = JSON.parse(document.querySelector('[data-ksg-variants]')?.textContent || '[]');
  } catch {
    variants = [];
  }

  /* Links become buttons. The href is the no-JS path and has done its job by
     the time this runs; leaving them as links would make every choice a
     navigation, which is the slow path this exists to replace. */
  for (const picker of pickers) {
    for (const link of picker.querySelectorAll('a.ksg-variant')) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = link.className;
      button.innerHTML = link.innerHTML;
      /* The whole dataset, not a list of the ones thought of at the time -
         data-variant-compare was added later and a named copy dropped it. */
      Object.assign(button.dataset, { ...link.dataset });
      if (link.hasAttribute('aria-current')) button.setAttribute('aria-pressed', 'true');
      link.replaceWith(button);
    }
  }

  /* --- the figures, re-asked rather than recalculated -------------------- */

  /* Re-rendered through the Section Rendering API rather than patched from a
     blob here. What a price box shows - the low-stock threshold, what the bar
     is measured against, whether a sale badge appears at all - is decided in
     Liquid, and a copy of those rules in JavaScript would be a second set to
     keep in step. This asks the same template the same question. */
  const section = document.querySelector('.ksg-page')?.closest('.shopify-section');
  const sectionId = section ? section.id.replace('shopify-section-', '') : null;
  let latest = null;

  const refresh = async (variantId) => {
    if (!sectionId) return;
    const token = {};
    latest = token;
    try {
      const url = `${location.pathname}?section_id=${encodeURIComponent(sectionId)}&variant=${variantId}`;
      const res = await fetch(url);
      /* A slow answer to a choice already replaced is worse than no answer. */
      if (!res.ok || latest !== token) return;
      const fresh = new DOMParser().parseFromString(await res.text(), 'text/html');

      const box = document.querySelector('.ksg-pricebox');
      const newBox = fresh.querySelector('.ksg-pricebox');
      if (box && newBox) box.replaceWith(newBox);

      /* Updated in place rather than swapped: replacing the row would tear
         the field out from under the stepper that is wired to it. */
      const wrap = document.querySelector('.ksg-qty');
      const input = wrap ? wrap.querySelector('input[name="quantity"]') : null;
      const newWrap = fresh.querySelector('.ksg-qty');
      if (wrap && input && newWrap) {
        const max = newWrap.querySelector('input')?.getAttribute('max');
        if (max) input.setAttribute('max', max);
        else input.removeAttribute('max');

        const limit = newWrap.getAttribute('data-limit');
        if (limit) wrap.setAttribute('data-limit', limit);
        else wrap.removeAttribute('data-limit');

        /* Hands the stepper back to ksg-qty.js: it clamps a quantity that no
           longer fits and re-reads the ends of the range. */
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } catch {
      /* Keep the figures already on the page rather than blanking them. */
    }
  };

  /* --- the photograph follows the choice --------------------------------- */

  /* The gallery is a scroll-snap track of every image, and each variant's
     image is one of its frames. Choosing a scent and still looking at another
     one's bottle is the kind of small wrongness that makes a page feel
     broken, so the track moves to the frame whose image matches.

     behavior instant for the reason recorded in ksg-gallery.js: a smooth
     programmatic scroll inside this mandatory snap container never
     completes. */
  const track = document.querySelector('.ksg-pdp__track');

  const showImage = (button) => {
    if (!track) return;
    const swatch = button.querySelector('.ksg-variant__img');
    const src = swatch && (swatch.currentSrc || swatch.src);
    if (!src) return;
    /* Matched on the file, not the URL: the swatch is served at 120px wide
       and the frame at up to 1200, so the query strings differ. */
    const file = src.split('?')[0].split('/').pop();
    const frame = [...track.children].find((f) => {
      const img = f.querySelector('img');
      return img && img.src.split('?')[0].split('/').pop() === file;
    });
    if (!frame) return;
    const f = frame.getBoundingClientRect();
    const t = track.getBoundingClientRect();
    track.scrollTo({ left: track.scrollLeft + (f.left - t.left), behavior: 'instant' });
  };

  /* --- nothing is chosen until someone chooses it ------------------------ */

  /* Shopify hands the page selected_or_first_available_variant, so a product
   * arrives with its first scent already selected and the buy bar ready to
   * add it. On a phone the swatches are below the fold and the bar is pinned
   * to the bottom, so the shopper who taps add without scrolling buys
   * Bergamot because it is alphabetically unlucky - and nothing on screen
   * ever told them there were twelve.
   *
   * That is the same hazard the bottom sheet was built for, and the answer
   * does not need a second surface either. The default is simply withdrawn:
   * with a real choice to make, no swatch starts marked, and add asks for
   * one instead of assuming it.
   *
   * The server still renders the default, which is what keeps the no-script
   * floor working - without JavaScript this is an ordinary Shopify form on
   * an ordinary preselected variant. This only withdraws the default where
   * there is a script running to ask for a replacement.
   *
   * A ?variant= in the URL IS a choice - from a collection card, a shared
   * link, a reload after choosing - so those arrive already decided and are
   * not asked again. */
  const params = new URLSearchParams(location.search);
  const decided = pickers.map(() => params.has('variant'));
  const allDecided = () => decided.every(Boolean);

  if (!allDecided()) {
    for (const picker of pickers) {
      for (const el of picker.querySelectorAll('.ksg-variant')) {
        el.classList.remove('ksg-variant--active');
        el.removeAttribute('aria-pressed');
      }
      /* The label says what is still wanted rather than naming a scent the
         shopper has not picked. The wording comes from the locale files by
         way of a data attribute - no Thai in here. */
      const label = picker.querySelector('[data-ksg-chosen]');
      if (label) {
        label.textContent = picker.dataset.ksgChooseLabel || '';
        label.classList.add('ksg-variants__chosen--asking');
      }
    }
  }

  /* Add, and buy now, ask for the missing choice instead of guessing it.
     Capture on the form so this runs before the add-to-cart module's own
     listener, which is on the document and therefore later. */
  let asking = null;

  form.addEventListener(
    'submit',
    (event) => {
      if (allDecided()) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const first = pickers[decided.findIndex((d) => !d)] || pickers[0];

      /* instant, not smooth. A smooth programmatic scroll does not complete
         in this theme - the same thing recorded in ksg-gallery.js and worked
         around in ksg-header.js - and here the cost was the whole point of
         the prompt: measured on a 375px phone with the picker scrolled off,
         add correctly refused to add and then the page did not move, so the
         button simply appeared dead. A jump is not elegant; being ignored is
         worse. */
      first.scrollIntoView({ block: 'center', behavior: 'instant' });

      /* An outline rather than an animation: ksg-motion.css switches every
         animation off under prefers-reduced-motion, and a prompt that only
         exists as movement would vanish for exactly the readers most likely
         to need it. */
      clearTimeout(asking);
      first.classList.add('ksg-variants--asking');
      asking = setTimeout(() => first.classList.remove('ksg-variants--asking'), 2600);

      first.querySelector('.ksg-variant')?.focus();
    },
    true
  );

  /* --- choosing ---------------------------------------------------------- */

  const choose = (button) => {
    const group = button.closest('.ksg-variants__opts');
    for (const el of group.querySelectorAll('.ksg-variant')) {
      el.classList.toggle('ksg-variant--active', el === button);
      if (el === button) el.setAttribute('aria-pressed', 'true');
      else el.removeAttribute('aria-pressed');
    }

    const chosen = button.querySelector('.ksg-variant__name')?.textContent.trim() || '';
    const picker = button.closest('.ksg-variants');
    const label = picker?.querySelector('[data-ksg-chosen]');
    if (label) {
      label.textContent = chosen;
      label.classList.remove('ksg-variants__chosen--asking');
    }

    /* This group has been answered. Once every group has, add stops asking. */
    const index = pickers.indexOf(picker);
    if (index > -1) decided[index] = true;
    picker?.classList.remove('ksg-variants--asking');

    /* Resolved against what every group is now showing, not just the one that
       was clicked. With a single option this is the same answer the button
       already carried; with two it is the difference between the scent on
       screen and a different one in the cart. */
    const combination = pickers.map(
      (g) => g.querySelector('.ksg-variant--active .ksg-variant__name')?.textContent.trim() || ''
    );
    const match = variants.find(
      (v) => v.options.length === combination.length && v.options.every((o, i) => o === combination[i])
    );

    const id = match ? String(match.id) : button.dataset.variantId;
    if (id) {
      idField.value = id;
      refresh(id);
      /* So a reload, a share or a back keeps the chosen variant. replaceState
         rather than pushState: a colour is not a page, and stacking a history
         entry per swatch is exactly what made the gallery's thumbnails break
         the back button. */
      const url = new URL(location.href);
      url.searchParams.set('variant', id);
      history.replaceState(history.state, '', url);
    }

    showImage(button);
  };

  for (const picker of pickers) {
    picker.addEventListener('click', (event) => {
      const button = event.target.closest('button.ksg-variant');
      if (!button || button.classList.contains('ksg-variant--unavailable')) return;
      choose(button);
    });
  }
}
